import { useState } from 'react';
import { MapPin, Star, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@hooks/useAuth';
import { usePlanRestrictions } from '@hooks/usePlanRestrictions';
import { getImageUrl } from '@utils/imageUtils';
import Button from '@components/common/Button';
import FeatureRestrictionModal from '@components/common/FeatureRestrictionModal';
import { ROUTES } from '@constants';

const StudentCard = ({ student }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { canAccessFeature } = usePlanRestrictions();
  const [showRestrictionModal, setShowRestrictionModal] = useState(false);

  const isCurrentUser = user?.id === student?.id;

  const handleViewPortfolio = () => {
    if (isCurrentUser) {
      navigate(`/portfolio/${student.id}`);
      return;
    }

    if (!canAccessFeature('view_others_portfolio')) {
      setShowRestrictionModal(true);
      return;
    }

    navigate(`/portfolio/${student.id}`);
  };

  const handleUpgrade = () => {
    setShowRestrictionModal(false);
    navigate(ROUTES.STUDENT_PLAN_UPGRADE);
  };

  const getLocation = () => {
    if (student?.address?.city && student?.address?.district) {
      return `${student.address.city}, ${student.address.district}`;
    } else if (student?.address?.city) {
      return student.address.city;
    }
    return null;
  };

  const getSkills = () => {
    return student?.skills?.slice(0, 4) || [];
  };

  const getMatchPercentage = () => {
    return student?.matchPercentage || 92;
  };

  const getInitials = () => {
    if (student?.firstName && student?.lastName) {
      return `${student.firstName[0]}${student.lastName[0]}`.toUpperCase();
    }
    return student?.email?.charAt(0).toUpperCase() || 'U';
  };

  const displayName =
    student?.firstName && student?.lastName
      ? `${student.firstName} ${student.lastName}`
      : student?.email?.split('@')[0] || student?.email;

  // Desktop-only: capped lists with "+N" overflow chip, based on FULL arrays
  const allSkills = student?.skills || [];
  const allInterests = student?.interestedIn || [];
  const visibleSkills = allSkills.slice(0, 3);
  const remainingSkills = allSkills.length - visibleSkills.length;
  const visibleInterests = allInterests.slice(0, 2);
  const remainingInterests = allInterests.length - visibleInterests.length;

  return (
    <>
      {/* ---------- MOBILE: Compact List Row (below sm) ---------- */}
      <div
        onClick={handleViewPortfolio}
        className="sm:hidden bg-white rounded-lg shadow-sm active:bg-gray-50 transition-colors duration-150 p-3 border border-gray-100 flex items-center gap-3 cursor-pointer"
      >
        {/* Avatar with online dot */}
        <div className="relative shrink-0">
          {student?.profilePicture ? (
            <img
              src={getImageUrl(student.profilePicture)}
              alt={displayName}
              className="w-12 h-12 rounded-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          <div
            className="w-12 h-12 rounded-full items-center justify-center"
            style={{
              display: student?.profilePicture ? 'none' : 'flex',
              background: 'linear-gradient(135deg, #2c3e5d 0%, #1e2a4a 100%)'
            }}
          >
            <span className="text-sm font-bold text-white">{getInitials()}</span>
          </div>
          <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-sm font-bold text-gray-900 truncate">{displayName}</h3>
            <div className="flex items-center gap-0.5 bg-green-50 border border-green-200 px-1.5 py-0.5 rounded-full shrink-0">
              <Star className="w-2.5 h-2.5 text-amber-400 fill-amber-400" />
              {/* <span className="text-[9px] font-semibold text-green-700">{getMatchPercentage()}%</span> */}
            </div>
          </div>

          <div className="flex items-center gap-1 text-[11px] text-gray-500 mt-0.5 truncate">
            <MapPin className="w-3 h-3 text-blue-500 shrink-0" />
            <span className="truncate">{student?.currentDesignation || 'Not specified'}</span>
          </div>

          <div className="flex items-center gap-1 text-[11px] text-gray-500 mt-0.5 truncate">
            <MapPin className="w-3 h-3 text-blue-500 shrink-0" />
            <span className="truncate">{getLocation() || 'Not specified'}</span>
          </div>

          {getSkills().length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1.5">
              {getSkills().slice(0, 2).map((skill, index) => (
                <span
                  key={index}
                  className="px-1.5 py-0.5 bg-gray-100 text-gray-700 text-[9px] rounded-full font-medium"
                >
                  {skill}
                </span>
              ))}
              {getSkills().length > 2 && (
                <span className="px-1.5 py-0.5 text-gray-400 text-[9px] font-medium">
                  +{getSkills().length - 2}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Chevron */}
        <ChevronRight className="w-4 h-4 text-gray-300 shrink-0" />
      </div>

      {/* ---------- DESKTOP / TABLET: Card Layout (sm and up) ---------- */}
      <div className="hidden sm:flex sm:flex-col h-full bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-200 p-4 border border-gray-100">
        {/* Available Badge — its own row, top-right (doesn't steal width from name anymore) */}
        <div className="flex justify-end mb-3">
          <div className="flex items-center gap-1.5 bg-white border border-gray-200 px-2.5 py-1 rounded-full">
            <span className="w-1.5 h-1.5 bg-green-500 rounded-full shrink-0"></span>
            <span className="text-xs font-medium text-gray-600 whitespace-nowrap">Available</span>
          </div>
        </div>

        {/* Header: Avatar + Name / Role / Location — gets the card's full width */}
        <div className="flex items-center gap-3 mb-4">
          <div className="shrink-0">
            {student?.profilePicture ? (
              <img
                src={getImageUrl(student.profilePicture)}
                alt={displayName}
                className="w-14 h-14 rounded-full object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <div
              className="w-14 h-14 rounded-full items-center justify-center"
              style={{
                display: student?.profilePicture ? 'none' : 'flex',
                background: 'linear-gradient(135deg, #2c3e5d 0%, #1e2a4a 100%)'
              }}
            >
              <span className="text-lg font-bold text-white">{getInitials()}</span>
            </div>
          </div>

          <div className="min-w-0 flex-1">
            <h3 className="text-base font-bold text-gray-900 truncate" title={displayName}>
              {displayName}
            </h3>
            <p className="text-sm text-gray-500 truncate">
              {student?.currentDesignation || 'Student'}
            </p>
            <div className="flex items-center gap-1 text-xs text-gray-400 mt-0.5 truncate">
              <MapPin className="w-3 h-3 shrink-0" />
              <span className="truncate">{getLocation() || 'Not specified'}</span>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-200 mb-4"></div>

        {/* Body: grows so every card's button lands on the same baseline in a grid row */}
        <div className="flex-1">
          {/* Skills */}
          <div className="mb-4">
            <p className="text-sm font-semibold text-gray-900 mb-2">Skills</p>
            <div className="flex flex-wrap gap-2">
              {visibleSkills.length > 0 ? (
                <>
                  {visibleSkills.map((skill, index) => (
                    <span
                      key={index}
                      className="px-2.5 py-1 bg-gray-100 text-gray-700 text-xs rounded-full font-medium whitespace-nowrap"
                    >
                      {skill}
                    </span>
                  ))}
                  {remainingSkills > 0 && (
                    <span className="px-2.5 py-1 bg-gray-50 text-gray-400 text-xs rounded-full font-medium">
                      +{remainingSkills}
                    </span>
                  )}
                </>
              ) : (
                <span className="text-xs text-gray-400">Not specified</span>
              )}
            </div>
          </div>

          {/* Interested In */}
          <div className="mb-2">
            <p className="text-sm font-semibold text-gray-900 mb-2">Interested In</p>
            <div className="flex flex-wrap gap-2">
              {visibleInterests.length > 0 ? (
                <>
                  {visibleInterests.map((interest, index) => (
                    <span
                      key={index}
                      className="px-2.5 py-1 bg-green-50 text-green-700 text-xs rounded-full font-medium whitespace-nowrap"
                    >
                      {interest}
                    </span>
                  ))}
                  {remainingInterests > 0 && (
                    <span className="px-2.5 py-1 bg-gray-50 text-gray-400 text-xs rounded-full font-medium">
                      +{remainingInterests}
                    </span>
                  )}
                </>
              ) : (
                <span className="text-xs text-gray-400">Not specified</span>
              )}
            </div>
          </div>
        </div>

        {/* View Portfolio Button — always bottom-aligned via flex-col + mt-auto behavior of flex-1 above */}
        <button
          onClick={handleViewPortfolio}
          className="w-full bg-navbar hover:bg-navbar-hover text-white font-semibold py-3 rounded-lg transition-colors duration-200 mt-4"
        >
          View Portfolio →
        </button>
      </div>

      {/* Feature Restriction Modal */}
      <FeatureRestrictionModal
        isOpen={showRestrictionModal}
        onClose={() => setShowRestrictionModal(false)}
        onUpgrade={handleUpgrade}
        featureName="View Others' Portfolio"
      />
    </>
  );
};

export default StudentCard;